import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ListPage, type ListPageColumn } from '@/components/templates/list-page'
import {
  ListPager,
  STAFF_LIST_PAGE_SIZE,
  useCursorPages,
  type PagedResponse,
} from '@/components/templates/list-pager'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON } from '@/lib/api'
import { displayDate } from '@/lib/dates'
import type { MembershipLevel, MembershipLevelsResponse } from '@/pages/MembershipLevelsPage.types'

type MemberStatus = 'Active' | 'Lapsed' | 'Suspended' | 'Pending-New'
type StatusFilter = 'all' | MemberStatus | 'overdue'
type KindFilter = 'all' | 'person' | 'organization'

export interface MemberRow {
  id: number
  person_id: number | null
  person_name: string | null
  organization_id: number | null
  organization_name: string | null
  kind: 'person' | 'organization'
  level: string
  status: MemberStatus
  member_number: number | null
  applied_on: string | null
  member_since_on: string | null
  renewal_due_on: string | null
  overdue: boolean
}

interface MembershipsResponse extends PagedResponse {
  memberships: MemberRow[]
  membership_counts?: {
    memberships: number
    people_with_member_status: number
  }
}

export interface MembersListPageProps {
  onOpenMember: (member: MemberRow) => void
}

const statusVariant: Record<MemberStatus, BadgeProps['variant']> = {
  Active: 'success',
  Lapsed: 'warning',
  Suspended: 'neutral',
  'Pending-New': 'warning',
}

const columns: ListPageColumn<MemberRow>[] = [
  {
    id: 'member',
    header: 'Member',
    cell: (member) => (
      <div className="flex flex-col">
        <span className="text-foreground">{member.organization_name ?? member.person_name}</span>
        <MemberContext member={member} />
      </div>
    ),
  },
  {
    id: 'level',
    header: 'Level',
    cell: (member) => <span className="text-muted-foreground">{member.level}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    cell: (member) => <Badge variant={statusVariant[member.status]}>{member.status}</Badge>,
  },
  {
    id: 'renewal',
    header: 'Date',
    cell: (member) => <MembershipDate member={member} />,
  },
]

export function MembersListPage({ onOpenMember }: MembersListPageProps) {
  const [status, setStatus] = React.useState<StatusFilter>('all')
  const [levelId, setLevelId] = React.useState('all')
  const [kind, setKind] = React.useState<KindFilter>('all')
  const filters = React.useMemo(() => ({ status, levelId, kind }), [status, levelId, kind])
  const { query: membersQuery, pager } = useCursorPages<MembershipsResponse>({
    queryKey: ['members'],
    path: membershipsPath(filters),
    limit: STAFF_LIST_PAGE_SIZE,
  })
  const levelsQuery = useQuery({
    queryKey: ['membership-levels', 'members-filter'],
    queryFn: () => getJSON<MembershipLevelsResponse>('/api/v1/membership_levels'),
    retry: false,
  })
  const rows = membersQuery.data?.memberships ?? []
  const counts = membersQuery.data?.membership_counts
  const hasFilters = status !== 'all' || levelId !== 'all'

  return (
    <ListPage<MemberRow>
      title="Members"
      description={
        counts
          ? `Memberships: ${counts.memberships} · People with member status: ${counts.people_with_member_status}`
          : undefined
      }
      toolbar={
        <MembersToolbar
          status={status}
          levelId={levelId}
          levels={levelsQuery.data?.membership_levels ?? []}
          onStatusChange={setStatus}
          onLevelChange={setLevelId}
          kind={kind}
          onKindChange={setKind}
        />
      }
      table={{
        rows,
        columns,
        getRowId: (member) => member.id,
        getRowLabel: (member) => member.organization_name ?? member.person_name ?? '',
        onRowOpen: onOpenMember,
        state: tableState(membersQuery.isLoading, membersQuery.isError),
        loadingTitle: 'Loading Members',
        emptyTitle: hasFilters ? 'No matching Members' : 'No Members yet',
        errorTitle: 'Members unavailable',
        header: <ListPager pager={pager} label="Members" position="top" />,
        footer: <ListPager pager={pager} label="Members" position="bottom" />,
      }}
    />
  )
}

function MembersToolbar({
  status,
  levelId,
  levels,
  onStatusChange,
  onLevelChange,
  kind,
  onKindChange,
}: {
  status: StatusFilter
  levelId: string
  levels: MembershipLevel[]
  onStatusChange: (status: StatusFilter) => void
  onLevelChange: (levelId: string) => void
  kind: KindFilter
  onKindChange: (kind: KindFilter) => void
}) {
  return (
    <>
      <Select value={kind} onValueChange={(value) => onKindChange(value as KindFilter)}>
        <SelectTrigger aria-label="Kind" className="w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All kinds</SelectItem>
          <SelectItem value="person">People</SelectItem>
          <SelectItem value="organization">Organizations</SelectItem>
        </SelectContent>
      </Select>
      <Select value={status} onValueChange={(value) => onStatusChange(value as StatusFilter)}>
        <SelectTrigger aria-label="Status" className="w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="Active">Active</SelectItem>
          <SelectItem value="Pending-New">Pending approval</SelectItem>
          <SelectItem value="Lapsed">Lapsed</SelectItem>
          <SelectItem value="Suspended">Suspended</SelectItem>
          <SelectItem value="overdue">Overdue</SelectItem>
        </SelectContent>
      </Select>
      <Select value={levelId} onValueChange={onLevelChange}>
        <SelectTrigger aria-label="Level" className="w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All levels</SelectItem>
          {levels.map((level) => (
            <SelectItem key={level.id} value={String(level.id)}>
              {level.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  )
}

function MembershipDate({ member }: { member: MemberRow }) {
  if (member.status === 'Pending-New') {
    return <span className="text-muted-foreground">{displayDate(member.applied_on)}</span>
  }

  const label = displayDate(member.renewal_due_on)
  if (!member.overdue) return <span className="text-muted-foreground">{label}</span>

  return (
    <span className="text-warning">
      {label} <span className="text-caption">· overdue</span>
    </span>
  )
}

function MemberContext({ member }: { member: MemberRow }) {
  if (member.status === 'Pending-New') {
    return (
      <>
        <span className="hidden text-caption text-muted-foreground sm:inline">
          {memberNumberLabel(member.member_number)}
        </span>
        <span className="text-caption text-muted-foreground sm:hidden">
          Applied {displayDate(member.applied_on)}
        </span>
      </>
    )
  }

  return (
    <span className="text-caption text-muted-foreground">
      {memberNumberLabel(member.member_number)}
    </span>
  )
}

function membershipsPath(filters: { status: StatusFilter; levelId: string; kind: KindFilter }) {
  const params = new URLSearchParams()
  if (filters.levelId !== 'all') params.set('membership_level_id', filters.levelId)
  if (filters.status === 'overdue') params.set('overdue', 'true')
  if (filters.status !== 'all' && filters.status !== 'overdue') params.set('status', filters.status)
  if (filters.kind !== 'all') params.set('kind', filters.kind)

  const query = params.toString()
  return query ? `/api/v1/memberships?${query}` : '/api/v1/memberships'
}

function tableState(isLoading: boolean, isError: boolean) {
  if (isLoading) return 'loading'
  if (isError) return 'error'

  return 'ready'
}

function memberNumberLabel(memberNumber: number | null) {
  return memberNumber ? `#${memberNumber}` : '-'
}
